import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import {
	Wrapper,
	ReviewCount,
} from './ratingStarsStyles';

import StarHalf from 'core/icons/StarHalf';
import { plural } from 'core/utils/stringUtils';

const REVIEWS_PER_PAGE = 10;

/**
 * Return a range of numbers from 1...size
 * e.g. `range(31)` will return `["1",...,"31"]`
 * @param {Number} size
 * @return {Array} Array of numbers from 1...size
 */
function range(size) {
	return [...Array(size).keys()].map(i => (i + 1).toString());
}

/**
 * Split decimal amount of stars into
 * whole number amount of stars, half stars, and empty stars
 *
 * @param {Float} stars - actual num stars
 * @param {Number} max - possible num of stars
 * @return {Array[Number]} [fullStars, halfStars, emptyStars]
 */
function splitRating(stars, max = 5) {
	if (stars >= max) return [max, 0, 0];

	const upperBound = 0.75, lowerBound = 0.25;
	let fullStars = Math.floor(stars);
	const decimal = stars - fullStars;
	const halfStars = decimal >= lowerBound && decimal <= upperBound ? 1 : 0;
	if (decimal > upperBound) {
		fullStars += 1;
	}
	const emptyStars = max - (fullStars + halfStars);

	return [fullStars, halfStars, emptyStars];
}

const RatingStars = ({
	slug,
	stars,
	reviews,
	stacked,
	tiny,
	short,
	starsOnly,
}) => {
	const showAsLink = slug && (+reviews > REVIEWS_PER_PAGE);
	const countProps = !showAsLink ? {} : {
		as: 'a',
		href: `/subscription-box/${slug}/reviews/`,
	};
	const [fullStars, halfStars, emptyStars] = splitRating(stars || 0);
	const halfStarDims = tiny ? 10 : 14;
	return (
		<Wrapper stacked={stacked} tiny={tiny}>
			{range(fullStars).map(x => <Fragment key={`full-star-${x}`}>{'★'}</Fragment>)}
			{halfStars > 0 ? <StarHalf width={halfStarDims} height={halfStarDims} /> : null}
			{range(emptyStars).map(x => <Fragment key={`empty-star-${x}`}>{'☆'}</Fragment>)}
			{!!reviews && !starsOnly && (
				<ReviewCount {...countProps}>
					{reviews} {!short && plural('review', reviews)}
				</ReviewCount>
			)}
		</Wrapper>
	);
};

RatingStars.propTypes = {
	// Only pass slug if you want the count to display as link
	slug: PropTypes.string,
	stars: PropTypes.number,
	reviews: PropTypes.number,
	stacked: PropTypes.bool,
	tiny: PropTypes.bool,
	short: PropTypes.bool,
	starsOnly: PropTypes.bool,
};

RatingStars.defaultProps = {
	wrapper: Wrapper,
	stars: 0,
	reviews: 0,
	stacked: false,
	tiny: false,
	short: false,
	starsOnly: false,
};

export default RatingStars;
